import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Calculator, Percent, Tag, TrendingUp, TrendingDown, DollarSign, Divide, ArrowRight, Copy, Zap, Activity, Cable, Lightbulb, Waves, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const s = String(v).replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

const copy = (val) => {
  try {
    navigator.clipboard.writeText(String(val));
    toast.success('Kopyalandı');
  } catch (_) {}
};

/* Ortak yenile butonu */
function ResetButton({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      title="Alanları temizle" type="button"
      className="ml-auto flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition">
      <RotateCcw className="h-3 w-3" />Yenile
    </button>
  );
}

/* --------------------- KDV HESAPLAMA --------------------- */
function KdvCard() {
  const [mode, setMode] = useState('haric'); // haric → dahil, dahil → haric
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState(20);
  const n = parseNum(amount);
  const r = parseNum(rate);
  let subtotal = 0, tax = 0, total = 0;
  if (mode === 'haric') {
    subtotal = n;
    tax = (n * r) / 100;
    total = subtotal + tax;
  } else {
    total = n;
    subtotal = n / (1 + r / 100);
    tax = total - subtotal;
  }
  const reset = () => { setAmount(''); setRate(20); setMode('haric'); };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Percent className="h-4 w-4 text-emerald-600" />KDV Hesaplama
          <ResetButton onClick={reset} testid="kdv-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <button onClick={() => setMode('haric')}
            className={`flex-1 text-xs py-1.5 rounded-md transition ${mode === 'haric' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}
            data-testid="kdv-mode-haric">
            KDV Hariç → Dahil
          </button>
          <button onClick={() => setMode('dahil')}
            className={`flex-1 text-xs py-1.5 rounded-md transition ${mode === 'dahil' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}
            data-testid="kdv-mode-dahil">
            KDV Dahil → Hariç
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{mode === 'haric' ? 'KDV Hariç Tutar (TL)' : 'KDV Dahil Tutar (TL)'}</Label>
            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0,00" data-testid="kdv-amount" />
          </div>
          <div>
            <Label className="text-xs">KDV Oranı (%)</Label>
            <div className="flex gap-1 items-center">
              <Input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} className="flex-1" data-testid="kdv-rate" />
              <div className="flex gap-0.5">
                {[1, 10, 20].map(x => (
                  <button key={x} onClick={() => setRate(x)}
                    className={`text-[10px] px-1.5 py-1 rounded border ${Number(rate) === x ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-300 hover:bg-slate-50'}`}>
                    %{x}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1.5">
          <ResultRow label="KDV Hariç" value={subtotal} />
          <ResultRow label={`KDV (%${r})`} value={tax} color="text-slate-600" />
          <div className="border-t border-slate-300 dark:border-slate-600 pt-2">
            <ResultRow label="KDV DAHİL" value={total} highlight />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- KÂR MARJI --------------------- */
function KarMarjiCard() {
  const [cost, setCost] = useState('');
  const [margin, setMargin] = useState(30);
  const c = parseNum(cost);
  const m = parseNum(margin);
  const sale = c * (1 + m / 100);
  const profit = sale - c;
  const reset = () => { setCost(''); setMargin(30); };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-amber-600" />Kâr Marjı ile Satış Fiyatı
          <ResetButton onClick={reset} testid="kar-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Alış / Maliyet (TL)</Label>
            <Input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} placeholder="0,00" data-testid="kar-cost" />
          </div>
          <div>
            <Label className="text-xs">Kâr Oranı (%)</Label>
            <Input type="number" step="0.01" value={margin} onChange={e => setMargin(e.target.value)} data-testid="kar-margin" />
          </div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="Kâr Tutarı" value={profit} color="text-amber-700" />
          <div className="border-t border-amber-200 dark:border-amber-800 pt-2">
            <ResultRow label="SATIŞ FİYATI" value={sale} highlight />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- İSKONTO --------------------- */
function IskontoCard() {
  const [price, setPrice] = useState('');
  const [disc, setDisc] = useState(10);
  const p = parseNum(price);
  const d = parseNum(disc);
  const discAmt = (p * d) / 100;
  const after = p - discAmt;
  const reset = () => { setPrice(''); setDisc(10); };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Tag className="h-4 w-4 text-rose-600" />İskonto (İndirim) Hesaplama
          <ResetButton onClick={reset} testid="isk-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Fiyat (TL)</Label>
            <Input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="0,00" data-testid="isk-price" />
          </div>
          <div>
            <Label className="text-xs">İskonto Oranı (%)</Label>
            <Input type="number" step="0.01" value={disc} onChange={e => setDisc(e.target.value)} data-testid="isk-rate" />
          </div>
        </div>
        <div className="bg-rose-50 dark:bg-rose-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="İskonto Tutarı" value={discAmt} color="text-rose-700" />
          <div className="border-t border-rose-200 dark:border-rose-800 pt-2">
            <ResultRow label="İSKONTOLU FİYAT" value={after} highlight />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- TAKSİT --------------------- */
function TaksitCard() {
  const [amount, setAmount] = useState('');
  const [count, setCount] = useState(3);
  const [rate, setRate] = useState(0); // vade farkı %
  const a = parseNum(amount);
  const c = Math.max(1, parseInt(count) || 1);
  const r = parseNum(rate);
  const total = a * (1 + r / 100);
  const monthly = total / c;
  const reset = () => { setAmount(''); setCount(3); setRate(0); };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Divide className="h-4 w-4 text-violet-600" />Taksit Hesaplama
          <ResetButton onClick={reset} testid="taksit-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Toplam Tutar (TL)</Label>
            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0,00" data-testid="taksit-amount" />
          </div>
          <div>
            <Label className="text-xs">Taksit Sayısı</Label>
            <Input type="number" min="1" value={count} onChange={e => setCount(e.target.value)} data-testid="taksit-count" />
          </div>
          <div>
            <Label className="text-xs">Vade Farkı (%)</Label>
            <Input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} />
          </div>
        </div>
        <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="Genel Toplam" value={total} color="text-violet-700" />
          <div className="border-t border-violet-200 dark:border-violet-800 pt-2">
            <ResultRow label={`AYLIK TAKSİT (${c} ay)`} value={monthly} highlight />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- BASİT DÖRT İŞLEM --------------------- */
function BasicCalc() {
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [waiting, setWaiting] = useState(false);

  const inputDigit = (d) => {
    if (waiting) { setDisplay(String(d)); setWaiting(false); }
    else setDisplay(display === '0' ? String(d) : display + d);
  };
  const inputDot = () => {
    if (waiting) { setDisplay('0.'); setWaiting(false); return; }
    if (!display.includes('.')) setDisplay(display + '.');
  };
  const doOp = (nextOp) => {
    const cur = parseFloat(display);
    if (prev == null) setPrev(cur);
    else if (op) {
      const res = calc(prev, cur, op);
      setDisplay(String(res));
      setPrev(res);
    }
    setOp(nextOp);
    setWaiting(true);
  };
  const calc = (a, b, o) => {
    switch (o) {
      case '+': return a + b;
      case '-': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? 0 : a / b;
      default: return b;
    }
  };
  const equals = () => {
    const cur = parseFloat(display);
    if (op != null && prev != null) {
      const res = calc(prev, cur, op);
      setDisplay(String(Math.round(res * 100) / 100));
      setPrev(null); setOp(null); setWaiting(true);
    }
  };
  const clear = () => { setDisplay('0'); setPrev(null); setOp(null); setWaiting(false); };
  const back = () => setDisplay(display.length > 1 ? display.slice(0, -1) : '0');

  const Btn = ({ children, onClick, className = '', testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`h-11 rounded-lg text-base font-semibold border transition active:scale-95 ${className}`}>
      {children}
    </button>
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4 text-slate-700" />Standart Hesap Makinesi
          <ResetButton onClick={clear} testid="basic-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-slate-900 text-white rounded-lg p-3 text-right text-2xl font-mono mb-3 min-h-[3rem] break-all">
          {display}
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Btn onClick={clear} className="bg-red-100 border-red-200 text-red-700 hover:bg-red-200" testid="calc-clear">C</Btn>
          <Btn onClick={back} className="bg-slate-100 border-slate-200 hover:bg-slate-200">⌫</Btn>
          <Btn onClick={() => copy(display)} className="bg-slate-100 border-slate-200 hover:bg-slate-200"><Copy className="h-4 w-4 mx-auto" /></Btn>
          <Btn onClick={() => doOp('÷')} className="bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200">÷</Btn>
          <Btn onClick={() => inputDigit(7)} className="bg-white border-slate-200 hover:bg-slate-50">7</Btn>
          <Btn onClick={() => inputDigit(8)} className="bg-white border-slate-200 hover:bg-slate-50">8</Btn>
          <Btn onClick={() => inputDigit(9)} className="bg-white border-slate-200 hover:bg-slate-50">9</Btn>
          <Btn onClick={() => doOp('×')} className="bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200">×</Btn>
          <Btn onClick={() => inputDigit(4)} className="bg-white border-slate-200 hover:bg-slate-50">4</Btn>
          <Btn onClick={() => inputDigit(5)} className="bg-white border-slate-200 hover:bg-slate-50">5</Btn>
          <Btn onClick={() => inputDigit(6)} className="bg-white border-slate-200 hover:bg-slate-50">6</Btn>
          <Btn onClick={() => doOp('-')} className="bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200">-</Btn>
          <Btn onClick={() => inputDigit(1)} className="bg-white border-slate-200 hover:bg-slate-50">1</Btn>
          <Btn onClick={() => inputDigit(2)} className="bg-white border-slate-200 hover:bg-slate-50">2</Btn>
          <Btn onClick={() => inputDigit(3)} className="bg-white border-slate-200 hover:bg-slate-50">3</Btn>
          <Btn onClick={() => doOp('+')} className="bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200">+</Btn>
          <Btn onClick={() => inputDigit(0)} className="bg-white border-slate-200 hover:bg-slate-50 col-span-2">0</Btn>
          <Btn onClick={inputDot} className="bg-white border-slate-200 hover:bg-slate-50">,</Btn>
          <Btn onClick={equals} className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" testid="calc-equals">=</Btn>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- YÜZDE / ORAN --------------------- */
function YuzdeCard() {
  const [base, setBase] = useState('');
  const [pct, setPct] = useState('');
  const [refPart, setRefPart] = useState('');
  const [refWhole, setRefWhole] = useState('');
  const b = parseNum(base); const p = parseNum(pct);
  const part = (b * p) / 100;
  const rp = parseNum(refPart); const rw = parseNum(refWhole);
  const ratio = rw > 0 ? (rp / rw) * 100 : 0;
  const reset = () => { setBase(''); setPct(''); setRefPart(''); setRefWhole(''); };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Percent className="h-4 w-4 text-sky-600" />Yüzde Hesaplama
          <ResetButton onClick={reset} testid="yuzde-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs text-slate-500 mb-1">Tutarın belirli bir yüzdesi</p>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" step="0.01" placeholder="Tutar" value={base} onChange={e => setBase(e.target.value)} />
            <Input type="number" step="0.01" placeholder="%" value={pct} onChange={e => setPct(e.target.value)} />
          </div>
          <div className="mt-2 text-sm bg-sky-50 dark:bg-sky-900/20 rounded p-2 flex items-center justify-between">
            <span className="text-slate-600">{fmt(b)} × %{fmt(p)}</span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <b className="text-sky-700 dark:text-sky-300">{fmt(part)}</b>
          </div>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">A tutarı, B'nin yüzde kaçı?</p>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" step="0.01" placeholder="A (parça)" value={refPart} onChange={e => setRefPart(e.target.value)} />
            <Input type="number" step="0.01" placeholder="B (bütün)" value={refWhole} onChange={e => setRefWhole(e.target.value)} />
          </div>
          <div className="mt-2 text-sm bg-sky-50 dark:bg-sky-900/20 rounded p-2 flex items-center justify-between">
            <span className="text-slate-600">{fmt(rp)} / {fmt(rw)}</span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <b className="text-sky-700 dark:text-sky-300">%{fmt(ratio)}</b>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- YARDIMCI --------------------- */
function ResultRow({ label, value, highlight = false, color = '', unit = 'TL' }) {
  return (
    <div className={`flex items-center justify-between ${highlight ? 'font-bold text-base' : 'text-sm'}`}>
      <span className={`${highlight ? 'text-slate-800 dark:text-slate-100' : (color || 'text-slate-600 dark:text-slate-300')}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`${highlight ? 'text-emerald-600 dark:text-emerald-400 text-lg' : 'text-slate-800 dark:text-slate-100'} tabular-nums`}>
          {fmt(value)} {unit}
        </span>
        <button onClick={() => copy(fmt(value))} className="text-slate-400 hover:text-slate-600" title="Kopyala">
          <Copy className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   ELEKTRİK HESAPLAMALARI
   ============================================================ */

/* --------------------- GÜÇ HESABI (V × A → W) --------------------- */
function GucHesabiCard() {
  const [phase, setPhase] = useState('1'); // 1 = tek faz AC, 3 = üç faz AC, dc = doğru akım
  const [volt, setVolt] = useState(220);
  const [amp, setAmp] = useState('');
  const [cosPhi, setCosPhi] = useState(0.95);
  const V = parseNum(volt); const I = parseNum(amp); const cf = parseNum(cosPhi);
  let watt = 0;
  if (phase === 'dc') watt = V * I;
  else if (phase === '1') watt = V * I * cf;
  else watt = Math.sqrt(3) * V * I * cf;
  const kW = watt / 1000;
  const beygirGucu = watt / 745.7; // HP dönüşümü
  const reset = () => { setPhase('1'); setVolt(220); setAmp(''); setCosPhi(0.95); };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-4 w-4 text-yellow-500" />Güç Hesabı (V·A → W / kW)
          <ResetButton onClick={reset} testid="guc-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          {[
            { k: '1', l: 'Tek Faz (220V)' },
            { k: '3', l: 'Üç Faz (380V)' },
            { k: 'dc', l: 'DC / Doğru Akım' },
          ].map(o => (
            <button key={o.k} onClick={() => { setPhase(o.k); if (o.k === '3') setVolt(380); else if (o.k === '1') setVolt(220); }}
              className={`flex-1 text-[11px] py-1.5 rounded-md transition ${phase === o.k ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}
              data-testid={`guc-phase-${o.k}`}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Gerilim (V)</Label>
            <Input type="number" step="0.01" value={volt} onChange={e => setVolt(e.target.value)} data-testid="guc-volt" />
          </div>
          <div>
            <Label className="text-xs">Akım (A)</Label>
            <Input type="number" step="0.01" value={amp} onChange={e => setAmp(e.target.value)} placeholder="0" data-testid="guc-amp" />
          </div>
          <div>
            <Label className="text-xs">cos φ (güç faktörü)</Label>
            <Input type="number" step="0.01" min="0.1" max="1" value={cosPhi} onChange={e => setCosPhi(e.target.value)} disabled={phase === 'dc'} />
          </div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="Watt (W)" value={watt} unit="W" color="text-yellow-800" />
          <ResultRow label="Beygir Gücü (HP)" value={beygirGucu} unit="HP" color="text-yellow-800" />
          <div className="border-t border-yellow-200 dark:border-yellow-800 pt-2">
            <ResultRow label="TOPLAM GÜÇ" value={kW} unit="kW" highlight />
          </div>
        </div>
        <p className="text-[10px] text-slate-500 italic">
          Formül: {phase === 'dc' ? 'P = V × I' : phase === '1' ? 'P = V × I × cosφ' : 'P = √3 × V × I × cosφ'}
        </p>
      </CardContent>
    </Card>
  );
}

/* --------------------- AKIM HESABI (W → A) --------------------- */
function AkimHesabiCard() {
  const [phase, setPhase] = useState('1');
  const [watt, setWatt] = useState('');
  const [volt, setVolt] = useState(220);
  const [cosPhi, setCosPhi] = useState(0.95);
  const W = parseNum(watt); const V = parseNum(volt); const cf = parseNum(cosPhi);
  let amp = 0;
  if (V > 0) {
    if (phase === 'dc') amp = W / V;
    else if (phase === '1') amp = cf > 0 ? W / (V * cf) : 0;
    else amp = cf > 0 ? W / (Math.sqrt(3) * V * cf) : 0;
  }
  const resetAkim = () => { setPhase('1'); setWatt(''); setVolt(220); setCosPhi(0.95); };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-orange-500" />Akım Hesabı (W → A)
          <ResetButton onClick={resetAkim} testid="akim-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          {[
            { k: '1', l: 'Tek Faz' },
            { k: '3', l: 'Üç Faz' },
            { k: 'dc', l: 'DC' },
          ].map(o => (
            <button key={o.k} onClick={() => { setPhase(o.k); if (o.k === '3') setVolt(380); else if (o.k === '1') setVolt(220); }}
              className={`flex-1 text-[11px] py-1.5 rounded-md transition ${phase === o.k ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Güç (Watt)</Label>
            <Input type="number" step="0.01" value={watt} onChange={e => setWatt(e.target.value)} placeholder="0" data-testid="akim-watt" />
          </div>
          <div>
            <Label className="text-xs">Gerilim (V)</Label>
            <Input type="number" step="0.01" value={volt} onChange={e => setVolt(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">cos φ</Label>
            <Input type="number" step="0.01" min="0.1" max="1" value={cosPhi} onChange={e => setCosPhi(e.target.value)} disabled={phase === 'dc'} />
          </div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="ÇEKİLEN AKIM" value={amp} unit="A" highlight />
        </div>
        <div className="grid grid-cols-4 gap-1 text-[10px]">
          {[500, 1000, 2000, 3000, 5000, 7500, 10000, 15000].map(w => {
            let a = 0;
            if (V > 0) {
              if (phase === 'dc') a = w / V;
              else if (phase === '1') a = cf > 0 ? w / (V * cf) : 0;
              else a = cf > 0 ? w / (Math.sqrt(3) * V * cf) : 0;
            }
            return (
              <button key={w} onClick={() => setWatt(w)}
                className="border border-slate-200 dark:border-slate-700 rounded px-1 py-1 text-slate-600 dark:text-slate-300 hover:bg-orange-50">
                <div className="font-semibold">{w >= 1000 ? `${w/1000}kW` : `${w}W`}</div>
                <div className="text-orange-600 font-mono">{a.toFixed(1)}A</div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- KABLO KESİTİ ÖNERİCİ --------------------- */
const CABLE_TABLE = [
  { size: 1.5, maxA: 16, kW220: 3.5, kW380: 10.5 },
  { size: 2.5, maxA: 25, kW220: 5.5, kW380: 16.5 },
  { size: 4, maxA: 32, kW220: 7.0, kW380: 21.0 },
  { size: 6, maxA: 40, kW220: 8.8, kW380: 26.4 },
  { size: 10, maxA: 63, kW220: 13.9, kW380: 41.5 },
  { size: 16, maxA: 80, kW220: 17.6, kW380: 52.7 },
  { size: 25, maxA: 100, kW220: 22.0, kW380: 65.9 },
  { size: 35, maxA: 125, kW220: 27.5, kW380: 82.3 },
  { size: 50, maxA: 160, kW220: 35.2, kW380: 105.4 },
  { size: 70, maxA: 200, kW220: 44.0, kW380: 131.7 },
  { size: 95, maxA: 240, kW220: 52.8, kW380: 158.1 },
];

function KabloKesitiCard() {
  const [mode, setMode] = useState('amp'); // amp veya watt
  const [amp, setAmp] = useState('');
  const [watt, setWatt] = useState('');
  const [phase, setPhase] = useState('1');
  const [length, setLength] = useState(20); // metre
  const [safetyPct, setSafetyPct] = useState(25);
  const V = phase === '1' ? 220 : 380;
  const rho = 0.0175; // Ω·mm²/m bakır

  let currentA = 0;
  if (mode === 'amp') currentA = parseNum(amp);
  else {
    const W = parseNum(watt);
    if (phase === '1') currentA = W / (V * 0.95);
    else currentA = W / (Math.sqrt(3) * V * 0.95);
  }
  const L = parseNum(length);
  const requiredA = currentA * (1 + parseNum(safetyPct) / 100);

  // Her kesit için değerlendirme
  const evaluations = CABLE_TABLE.map(row => {
    // Gerilim düşümü (V)
    const dv = row.size > 0
      ? (phase === '1' ? (2 * L * currentA * rho) / row.size : (Math.sqrt(3) * L * currentA * rho) / row.size)
      : 0;
    const dvPct = V > 0 ? (dv / V) * 100 : 0;
    const verim = Math.max(0, 100 - dvPct);
    const loadPct = row.maxA > 0 ? (requiredA / row.maxA) * 100 : 999;
    // Değerlendirme kategorisi
    let status;
    if (loadPct > 100) status = { key: 'unsafe', label: 'YETERSİZ (aşırı yük)', color: 'text-red-700 bg-red-50 border-red-300', bar: 'bg-red-500' };
    else if (dvPct > 5) status = { key: 'drop', label: 'YÜKSEK DÜŞÜM', color: 'text-red-700 bg-red-50 border-red-300', bar: 'bg-red-500' };
    else if (loadPct > 80 || dvPct > 3) status = { key: 'ok', label: 'KABUL EDİLEBİLİR', color: 'text-amber-700 bg-amber-50 border-amber-300', bar: 'bg-amber-500' };
    else status = { key: 'best', label: 'İDEAL', color: 'text-emerald-700 bg-emerald-50 border-emerald-300', bar: 'bg-emerald-500' };
    return { ...row, dv, dvPct, verim, loadPct, status };
  });

  // İdeal / önerilen: verimi en yüksek AMA en küçük kesit tercih (maliyet)
  const idealRow = evaluations.find(e => e.status.key === 'best')
    || evaluations.find(e => e.status.key === 'ok')
    || evaluations[evaluations.length - 1];

  const reset = () => { setAmp(''); setWatt(''); setLength(20); setSafetyPct(25); };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Cable className="h-4 w-4 text-blue-600" />Kablo Kesiti Önerici (Bakır - TSE)
          <ResetButton onClick={reset} testid="kablo-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
            <button onClick={() => setMode('amp')}
              className={`flex-1 text-xs py-1.5 rounded-md transition ${mode === 'amp' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
              Amper Gir
            </button>
            <button onClick={() => setMode('watt')}
              className={`flex-1 text-xs py-1.5 rounded-md transition ${mode === 'watt' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
              Watt Gir
            </button>
          </div>
          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
            <button onClick={() => setPhase('1')}
              className={`flex-1 text-xs py-1.5 rounded-md transition ${phase === '1' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
              Tek Faz (220V)
            </button>
            <button onClick={() => setPhase('3')}
              className={`flex-1 text-xs py-1.5 rounded-md transition ${phase === '3' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
              Üç Faz (380V)
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">{mode === 'amp' ? 'Akım (A)' : 'Güç (Watt)'}</Label>
            {mode === 'amp'
              ? <Input type="number" step="0.01" value={amp} onChange={e => setAmp(e.target.value)} placeholder="0" data-testid="kablo-amp" />
              : <Input type="number" step="0.01" value={watt} onChange={e => setWatt(e.target.value)} placeholder="0" data-testid="kablo-watt" />
            }
          </div>
          <div>
            <Label className="text-xs">Uzunluk (metre)</Label>
            <Input type="number" step="0.1" value={length} onChange={e => setLength(e.target.value)} placeholder="20" data-testid="kablo-length" />
          </div>
          <div>
            <Label className="text-xs">Güvenlik Payı (%)</Label>
            <Input type="number" step="1" value={safetyPct} onChange={e => setSafetyPct(e.target.value)} />
          </div>
        </div>
        {/* Özet */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[10px] text-slate-500">Yük Akımı</p>
            <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{fmt(currentA)} A</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500">Güvenlik ile</p>
            <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{fmt(requiredA)} A</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500">Kablo Boyu</p>
            <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{fmt(L)} m</p>
          </div>
        </div>
        {/* Önerilen */}
        {currentA > 0 && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500 rounded-lg p-3 text-center">
            <p className="text-[10px] text-emerald-700 dark:text-emerald-300 font-semibold uppercase tracking-wide">Önerilen Kablo Kesiti</p>
            <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">{idealRow.size} mm²</p>
            <div className="flex justify-center gap-4 text-[11px] text-slate-600 dark:text-slate-300 mt-1">
              <span>Verim: <b className="text-emerald-700">%{fmt(idealRow.verim)}</b></span>
              <span>Düşüm: <b>%{fmt(idealRow.dvPct)}</b></span>
              <span>Yük: <b>%{fmt(idealRow.loadPct)}</b></span>
            </div>
          </div>
        )}
        {/* Tüm kesit değerlendirmeleri */}
        {currentA > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Tüm Kesitlerde Değerlendirme</p>
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              {evaluations.map(e => (
                <div key={e.size} className={`flex items-center gap-2 p-2 rounded border ${e.size === idealRow.size ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-slate-200 dark:border-slate-700'}`}>
                  <div className="w-14 text-center">
                    <p className="font-bold text-sm text-slate-800 dark:text-slate-100">{e.size} mm²</p>
                    <p className="text-[9px] text-slate-500">Maks {e.maxA}A</p>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] text-slate-500">Verim</span>
                      <span className="text-[10px] font-bold text-slate-700 dark:text-slate-200">%{fmt(e.verim)}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full ${e.status.bar}`} style={{ width: `${Math.max(2, Math.min(100, e.verim))}%` }} />
                    </div>
                    <p className="text-[9px] text-slate-500 mt-0.5">
                      Düşüm: %{fmt(e.dvPct)} · Yük: %{fmt(e.loadPct)}
                    </p>
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${e.status.color} font-semibold whitespace-nowrap`}>{e.status.label}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              İdeal: %5'ten az gerilim düşümü + %80'den az yük · Bakır özdirenç: 0,0175 Ω·mm²/m
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* --------------------- GERİLİM DÜŞÜMÜ --------------------- */
function GerilimDusumuCard() {
  const [phase, setPhase] = useState('1');
  const [length, setLength] = useState(''); // meter
  const [amp, setAmp] = useState('');
  const [size, setSize] = useState(2.5); // mm²
  const [volt, setVolt] = useState(220);
  const rho = 0.0175; // Ω·mm²/m bakır özdirenç
  const L = parseNum(length); const I = parseNum(amp); const S = parseNum(size); const V = parseNum(volt);
  // Tek faz: ΔV = 2 × L × I × ρ / S
  // Üç faz:  ΔV = √3 × L × I × ρ / S
  let dv = 0;
  if (S > 0) {
    dv = phase === '1' ? (2 * L * I * rho) / S : (Math.sqrt(3) * L * I * rho) / S;
  }
  const dvPct = V > 0 ? (dv / V) * 100 : 0;
  const acceptable = dvPct <= 5; // TSE'ye göre %5 sınır
  const resetDusum = () => { setPhase('1'); setLength(''); setAmp(''); setSize(2.5); setVolt(220); };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-pink-600" />Gerilim Düşümü Hesabı
          <ResetButton onClick={resetDusum} testid="dusum-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <button onClick={() => { setPhase('1'); setVolt(220); }}
            className={`flex-1 text-xs py-1.5 rounded-md transition ${phase === '1' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
            Tek Faz
          </button>
          <button onClick={() => { setPhase('3'); setVolt(380); }}
            className={`flex-1 text-xs py-1.5 rounded-md transition ${phase === '3' ? 'bg-white dark:bg-slate-900 shadow font-semibold' : 'text-slate-500'}`}>
            Üç Faz
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Kablo Uzunluğu (m)</Label>
            <Input type="number" step="0.1" value={length} onChange={e => setLength(e.target.value)} placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Akım (A)</Label>
            <Input type="number" step="0.01" value={amp} onChange={e => setAmp(e.target.value)} placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Kesit (mm²)</Label>
            <select value={size} onChange={e => setSize(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
              {CABLE_TABLE.map(r => <option key={r.size} value={r.size}>{r.size} mm²</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Şebeke Gerilimi (V)</Label>
            <Input type="number" step="1" value={volt} onChange={e => setVolt(e.target.value)} />
          </div>
        </div>
        <div className={`rounded-lg p-3 space-y-1.5 ${acceptable ? 'bg-pink-50 dark:bg-pink-900/20' : 'bg-red-50 dark:bg-red-900/20 border border-red-300'}`}>
          <ResultRow label="Gerilim Düşümü" value={dv} unit="V" />
          <div className="border-t border-pink-200 dark:border-pink-800 pt-2">
            <ResultRow label="% Düşüm" value={dvPct} unit="%" highlight color={acceptable ? '' : 'text-red-600'} />
          </div>
          <p className={`text-[11px] mt-1 ${acceptable ? 'text-emerald-700' : 'text-red-600 font-semibold'}`}>
            {acceptable ? '✓ Kabul edilebilir (< %5)' : '⚠ Sınır aşıldı! Daha büyük kesit önerilir'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- ELEKTRİK MALİYETİ --------------------- */
function ElektrikMaliyetCard() {
  const [watt, setWatt] = useState('');
  const [hours, setHours] = useState(1);
  const [days, setDays] = useState(30);
  const [rate, setRate] = useState(3.15); // TL/kWh (Türkiye ortalama)
  const W = parseNum(watt); const h = parseNum(hours); const d = parseNum(days); const r = parseNum(rate);
  const kwh_gunluk = (W * h) / 1000;
  const kwh_aylik = kwh_gunluk * d;
  const gunluk_tl = kwh_gunluk * r;
  const aylik_tl = kwh_aylik * r;
  const yillik_tl = aylik_tl * 12;
  const resetMaliyet = () => { setWatt(''); setHours(1); setDays(30); setRate(3.15); };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-emerald-600" />Elektrik Tüketim Maliyeti
          <ResetButton onClick={resetMaliyet} testid="maliyet-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Cihaz Gücü (Watt)</Label>
            <Input type="number" step="0.01" value={watt} onChange={e => setWatt(e.target.value)} placeholder="1000" data-testid="maliyet-watt" />
          </div>
          <div>
            <Label className="text-xs">Günlük Çalışma (saat)</Label>
            <Input type="number" step="0.1" value={hours} onChange={e => setHours(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Ay içindeki Gün Sayısı</Label>
            <Input type="number" step="1" value={days} onChange={e => setDays(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Birim Fiyat (TL/kWh)</Label>
            <Input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} />
          </div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="Günlük Tüketim" value={kwh_gunluk} unit="kWh" />
          <ResultRow label="Aylık Tüketim" value={kwh_aylik} unit="kWh" />
          <div className="border-t border-emerald-200 dark:border-emerald-800 pt-2 mt-1">
            <ResultRow label="Günlük Maliyet" value={gunluk_tl} unit="TL" />
            <ResultRow label="Aylık Maliyet" value={aylik_tl} unit="TL" highlight />
            <ResultRow label="Yıllık Maliyet" value={yillik_tl} unit="TL" color="text-amber-700" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- OHM KANUNU --------------------- */
function OhmKanunuCard() {
  const [volt, setVolt] = useState('');
  const [amp, setAmp] = useState('');
  const [ohm, setOhm] = useState('');
  const [lastEdited, setLastEdited] = useState(null); // hangi ikisi girildi
  const V = parseNum(volt); const I = parseNum(amp); const R = parseNum(ohm);
  // Hangi iki değerin girili olduğuna göre üçüncüyü hesapla
  let outV = V, outI = I, outR = R;
  if (volt && amp && !ohm) outR = I > 0 ? V / I : 0;
  else if (volt && ohm && !amp) outI = R > 0 ? V / R : 0;
  else if (amp && ohm && !volt) outV = I * R;

  const clear = () => { setVolt(''); setAmp(''); setOhm(''); };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Waves className="h-4 w-4 text-indigo-600" />Ohm Kanunu (V = I × R)
          <ResetButton onClick={clear} testid="ohm-reset" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-slate-500">İki değeri girin, üçüncüsü hesaplanacak. Yeniden hesap için "Temizle" butonuna basın.</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Gerilim (V)</Label>
            <Input type="number" step="0.01" value={outV || ''} onChange={e => setVolt(e.target.value)} placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Akım (A)</Label>
            <Input type="number" step="0.01" value={outI || ''} onChange={e => setAmp(e.target.value)} placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Direnç (Ω)</Label>
            <Input type="number" step="0.01" value={outR || ''} onChange={e => setOhm(e.target.value)} placeholder="0" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={clear} className="w-full">Temizle</Button>
        </div>
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 space-y-1.5">
          <ResultRow label="Güç (P = V × I)" value={outV * outI} unit="W" highlight />
          <ResultRow label="Güç (P = I² × R)" value={outI * outI * outR} unit="W" color="text-indigo-700" />
        </div>
      </CardContent>
    </Card>
  );
}

/* --------------------- KATEGORİLER --------------------- */
const CATEGORIES = [
  { key: 'genel', label: 'Genel Hesaplama', icon: Calculator },
  { key: 'elektrik', label: 'Elektrik Hesaplama', icon: Zap },
];

export default function HesapHesaplamaPage() {
  const [category, setCategory] = useState('genel');

  return (
    <div className="space-y-4" data-testid="hesap-hesaplama-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Calculator className="h-6 w-6 text-emerald-600" />
          Hesaplama Aracı
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">Ticari ve elektriksel hesaplamalar — hepsi tek yerde</p>
      </div>

      {/* Kategori Sekmesi */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg w-fit">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          return (
            <button key={c.key} onClick={() => setCategory(c.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-2 ${category === c.key ? 'bg-white dark:bg-slate-900 shadow text-slate-800 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
              data-testid={`hesaplama-cat-${c.key}`}>
              <Icon className="h-4 w-4" />
              {c.label}
            </button>
          );
        })}
      </div>

      {category === 'genel' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <KdvCard />
          <KarMarjiCard />
          <IskontoCard />
          <TaksitCard />
          <YuzdeCard />
          <BasicCalc />
        </div>
      )}

      {category === 'elektrik' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GucHesabiCard />
          <AkimHesabiCard />
          <KabloKesitiCard />
          <GerilimDusumuCard />
          <ElektrikMaliyetCard />
          <OhmKanunuCard />
        </div>
      )}
    </div>
  );
}
